import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FacebookAdNZ from '../components/social/FacebookAdNZ';
import ContractorRecruitAd from '../components/social/ContractorRecruitAd';
import HerdDataSortedAd from '../components/social/HerdDataSortedAd';
import TaranakiRegionAd from '../components/social/TaranakiRegionAd';
import HeiferProjectAd from '../components/social/HeiferProjectAd';
import WhereWeAreNowAd from '../components/social/WhereWeAreNowAd';
import TeamShoutOutAd from '../components/social/TeamShoutOutAd';
import MatingWeightsAd from '../components/social/MatingWeightsAd';
import AdControls from '../components/social/AdControls';
import { useAdSettings } from '../hooks/useAdSettings';
import { RotateCcw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SocialAdsPage = () => {
  const navigate = useNavigate();
  const [savingAd, setSavingAd] = useState(null);
  const [savedAds, setSavedAds] = useState([]);
  const { overrides, archived, saveOverrides, archive, unarchive } = useAdSettings();

  // Fields editable for the 10-Week Heifer Project ad (props on HeiferProjectAd)
  const HEIFER_FIELDS = [
    { key: 'pillText',       label: 'Duration pill',    type: 'text',     placeholder: '10-Week Project · 2026' },
    { key: 'tagline',        label: 'Tagline (top)',    type: 'text',     placeholder: 'The 10-Week NZ' },
    { key: 'headlineLine1',  label: 'Headline line 1',  type: 'text',     placeholder: 'Heifer Weigh' },
    { key: 'headlineLine2',  label: 'Headline line 2',  type: 'text',     placeholder: 'Project' },
    { key: 'subheadline',    label: 'Sub-headline',     type: 'textarea', rows: 3, placeholder: 'From Kaitaia to Bluff — …' },
    { key: 'statWeeks',      label: 'Weeks number',     type: 'text',     placeholder: '10' },
    { key: 'statHeifers',    label: 'Heifers number',   type: 'text',     placeholder: '28,000' },
    { key: 'statFarms',      label: 'Farms number',     type: 'text',     placeholder: '250' },
    { key: 'ctaUrl',         label: 'CTA URL',          type: 'text',     placeholder: 'measuredlivestock.com' },
  ];

  const AD_TEMPLATES = [
    { key: 'mating_weights',      label: 'Mating Weights & Condition',  Component: MatingWeightsAd,   fields: [
      { key: 'pillText',       label: 'Pill text',        type: 'text',     placeholder: 'Pre-Mating Planning' },
      { key: 'tagline',        label: 'Tagline',          type: 'text',     placeholder: 'Thinking about' },
      { key: 'headlineLine1',  label: 'Headline line 1',  type: 'text',     placeholder: 'Mating Weights' },
      { key: 'headlineLine2',  label: 'Headline line 2',  type: 'text',     placeholder: '& Condition?' },
      { key: 'subheadline',    label: 'Sub-headline',     type: 'textarea', rows: 3, placeholder: 'Get in touch to line up your herd…' },
      { key: 'bullet1',        label: 'Bullet 1',         type: 'text',     placeholder: 'Live-weight capture' },
      { key: 'bullet2',        label: 'Bullet 2',         type: 'text',     placeholder: 'Body Condition Score' },
      { key: 'bullet3',        label: 'Bullet 3',         type: 'text',     placeholder: 'Clean CSV, same day' },
      { key: 'bullet4',        label: 'Bullet 4',         type: 'text',     placeholder: 'Every animal verified' },
      { key: 'contactLabel',   label: 'Contact label',    type: 'text',     placeholder: 'Line up your herd' },
      { key: 'contactValue',   label: 'Contact value',    type: 'text',     placeholder: '0800 MEASURE' },
      { key: 'ctaText',        label: 'CTA button text',  type: 'text',     placeholder: 'Get In Touch' },
      { key: 'ctaUrl',         label: 'CTA URL',          type: 'text',     placeholder: 'measuredlivestock.com' },
    ] },
    { key: 'team_shoutout',       label: 'Team Shout-Out',              Component: TeamShoutOutAd,    fields: [
      { key: 'pillText',       label: 'Pill text',        type: 'text',     placeholder: 'Team Shout-Out' },
      { key: 'tagline',        label: 'Tagline',          type: 'text',     placeholder: 'A Massive Shout-Out' },
      { key: 'headlineLine1',  label: 'Headline line 1',  type: 'text',     placeholder: 'To the Measured' },
      { key: 'headlineLine2',  label: 'Headline line 2',  type: 'text',     placeholder: 'Livestock Team' },
      { key: 'subheadline',    label: 'Sub-headline',     type: 'textarea', rows: 3, placeholder: 'Head down, gates open, yards humming…' },
      { key: 'region',         label: 'Region',           type: 'text',     placeholder: 'North Island' },
      { key: 'statDays',       label: 'Days number',      type: 'text',     placeholder: '15' },
      { key: 'statFarms',      label: 'Farms number',     type: 'text',     placeholder: '124' },
      { key: 'statCattle',     label: 'Cattle number',    type: 'text',     placeholder: '9,000+' },
      { key: 'ctaUrl',         label: 'CTA URL',          type: 'text',     placeholder: 'measuredlivestock.com' },
    ] },
    { key: 'where_we_are_now',    label: 'Where We Are Now',            Component: WhereWeAreNowAd,   fields: [] },
    { key: 'heifer_project',      label: '10-Week Heifer Project',      Component: HeiferProjectAd,   fields: HEIFER_FIELDS },
    { key: 'fb_nz_wide',          label: 'FB Ad — NZ Wide',             Component: FacebookAdNZ,      fields: [] },
    { key: 'contractor_recruit',  label: 'Contractor Recruit',          Component: ContractorRecruitAd, fields: [] },
    { key: 'herd_data_sorted',    label: 'Herd Data Sorted',            Component: HerdDataSortedAd,  fields: [] },
    { key: 'taranaki_region',     label: 'Taranaki Region',             Component: TaranakiRegionAd,  fields: [] },
  ];

  // Function to save ad to admin marketing section
  const saveAdToMarketing = async (adName, adType, platform) => {
    setSavingAd(adName);
    try {
      const adData = {
        name: adName,
        platform: platform,
        type: adType,
        image_url: `/social-ads#${adName.toLowerCase().replace(/\s+/g, '-')}`,
        notes: `${adType} - ${platform}. View on Social Ads page.`,
      };
      
      const response = await fetch(`${API_URL}/api/admin/marketing/ads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(adData),
      });
      
      if (response.ok) {
        setSavedAds(prev => [...prev, adName]);
        alert(`✅ "${adName}" saved to Admin Marketing section!`);
      } else {
        alert('Failed to save ad. Please try again.');
      }
    } catch (error) {
      console.error('Error saving ad:', error);
      alert('Error saving ad. Please try again.');
    }
    setSavingAd(null);
  };

  // Cattle ear tag image component
  const EarTagIcon = ({ size = "large", className = "" }) => (
    <img 
      src="https://images.unsplash.com/photo-1570042225831-d98fa7577f1e?w=100&h=100&fit=crop&crop=center"
      alt="Cattle ear tag"
      className={`inline-block object-cover rounded ${size === "large" ? "w-8 h-8" : "w-4 h-4"} ${className}`}
      style={{ objectPosition: 'center' }}
      onError={(e) => {
        // Fallback to emoji if image fails
        e.target.style.display = 'none';
        e.target.nextSibling.style.display = 'inline';
      }}
    />
  );

  // Yellow ear tag with number - SVG fallback
  const EarTagSvg = ({ size = "large", className = "" }) => (
    <svg 
      viewBox="0 0 40 50" 
      className={`inline-block ${size === "large" ? "w-8 h-10" : "w-4 h-5"} ${className}`}
    >
      {/* Tag shape - yellow ear tag */}
      <path 
        d="M5 8 Q5 2, 12 2 L28 2 Q35 2, 35 8 L35 42 Q35 48, 28 48 L12 48 Q5 48, 5 42 Z" 
        fill="#FFD700" 
        stroke="#DAA520" 
        strokeWidth="1"
      />
      {/* Hole at top */}
      <circle cx="20" cy="8" r="3" fill="#1a4d2e"/>
      {/* Number on tag */}
      <text x="20" y="32" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#000">42</text>
    </svg>
  );

  // Square Ad (1080x1080) - Facebook/Instagram Feed
  const SquareAd = () => (
    <div 
      className="relative bg-gradient-to-br from-[#1a4d2e] via-[#2d5a3d] to-[#1a4d2e] text-white"
      style={{ width: '540px', height: '540px' }}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-32 h-32 border-2 border-white rounded-full"></div>
        <div className="absolute bottom-20 right-10 w-24 h-24 border-2 border-white rounded-full"></div>
      </div>
      
      {/* Content */}
      <div className="relative h-full flex flex-col p-8">
        {/* Logo/Brand */}
        <div className="text-center mb-4">
          <h1 className="text-2xl font-bold tracking-wide">MEASURED LIVESTOCK</h1>
          <p className="text-xs text-white/80 tracking-widest">TRAIT COLLECTION SERVICE</p>
        </div>

        {/* Main Headline */}
        <div className="flex-1 flex flex-col justify-center text-center">
          <p className="text-[#ff6b00] text-sm font-semibold mb-2 tracking-wider">INTRODUCING PRICEHUB</p>
          <h2 className="text-3xl font-bold mb-4 leading-tight">
            Know Your Cost<br/>
            <span className="text-[#ff6b00]">Instantly</span>
          </h2>
          
          {/* Services Grid */}
          <div className="grid grid-cols-3 gap-2 my-4 text-xs">
            <div className="bg-white/10 rounded-lg p-2 backdrop-blur">
              <span className="text-lg">⚖️</span>
              <p className="font-semibold">Weigh</p>
            </div>
            <div className="bg-white/10 rounded-lg p-2 backdrop-blur flex flex-col items-center">
              <EarTagSvg size="large" />
              <p className="font-semibold">Tag</p>
            </div>
            <div className="bg-white/10 rounded-lg p-2 backdrop-blur">
              <span className="text-lg">📋</span>
              <p className="font-semibold">Record</p>
            </div>
            <div className="bg-white/10 rounded-lg p-2 backdrop-blur">
              <span className="text-lg">🔍</span>
              <p className="font-semibold">Scan</p>
            </div>
            <div className="bg-white/10 rounded-lg p-2 backdrop-blur">
              <span className="text-lg">🧪</span>
              <p className="font-semibold">Sample</p>
            </div>
            <div className="bg-white/10 rounded-lg p-2 backdrop-blur">
              <span className="text-lg">✅</span>
              <p className="font-semibold">Test</p>
            </div>
          </div>

          <p className="text-sm text-white/90 mt-2">
            CSV files formatted for third-party software & industry platforms
          </p>
          
          {/* Value Props */}
          <div className="flex justify-center gap-4 mt-3 text-xs">
            <span className="flex items-center gap-1">🚗 We come to you</span>
            <span className="flex items-center gap-1">📅 Your schedule</span>
            <span className="flex items-center gap-1">🤝 Extra hands</span>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <div className="inline-block bg-[#ff6b00] text-white font-bold py-3 px-8 rounded-lg text-lg">
            Plan Your Day Now →
          </div>
          <p className="text-xs mt-3 text-white/70">measuredlivestock.com</p>
        </div>
      </div>
    </div>
  );

  // Landscape Ad (1200x628) - Facebook Feed
  const LandscapeAd = () => (
    <div 
      className="relative bg-gradient-to-r from-[#1a4d2e] via-[#2d5a3d] to-[#1a4d2e] text-white"
      style={{ width: '600px', height: '314px' }}
    >
      {/* Content */}
      <div className="relative h-full flex p-6">
        {/* Left Side - Text */}
        <div className="flex-1 flex flex-col justify-center pr-4">
          <p className="text-[#ff6b00] text-xs font-semibold mb-1 tracking-wider">MEASURED LIVESTOCK</p>
          <h2 className="text-2xl font-bold mb-2 leading-tight">
            Select One or More Services.<br/>
            <span className="text-[#ff6b00]">See Prices Instantly.</span>
          </h2>
          <p className="text-sm text-white/90 mb-2">
            No waiting. No surprises. No hidden costs.
          </p>
          
          {/* Value Props */}
          <div className="flex gap-3 text-xs mb-3 text-white/80">
            <span>🚗 We come to you</span>
            <span>📅 Your schedule</span>
            <span>🤝 Extra hands</span>
          </div>
          
          {/* Services Row */}
          <div className="flex gap-2 text-xs mb-4">
            <span className="bg-white/20 px-2 py-1 rounded">⚖️ Weigh</span>
            <span className="bg-white/20 px-2 py-1 rounded inline-flex items-center gap-1"><EarTagSvg size="small" /> Tag</span>
            <span className="bg-white/20 px-2 py-1 rounded">📋 Record</span>
            <span className="bg-white/20 px-2 py-1 rounded">🔍 Scan</span>
          </div>

          <div className="inline-block bg-[#ff6b00] text-white font-bold py-2 px-6 rounded-lg text-sm w-fit">
            Plan Your Day Now →
          </div>
        </div>

        {/* Right Side - Pricing Preview */}
        <div className="w-48 flex flex-col justify-center">
          <div className="bg-white rounded-lg p-3 shadow-xl">
            <p className="text-[#1a4d2e] text-xs font-bold mb-2 text-center">YOUR TOTAL (excl. GST)</p>
            <p className="text-[#ff6b00] text-3xl font-bold text-center">$869.00</p>
            <p className="text-gray-500 text-xs text-center mt-1">+ GST • No Hidden Costs</p>
          </div>
          <p className="text-xs text-center mt-2 text-white/70">
            CSV files for third-party software
          </p>
        </div>
      </div>
    </div>
  );

  // Story Ad (1080x1920) - Instagram/Facebook Stories
  const StoryAd = () => (
    <div 
      className="relative overflow-hidden"
      style={{ width: '270px', height: '480px' }}
    >
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1500595046743-cd271d694d30?w=800')] bg-cover bg-center">
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a4d2e]/90 via-[#1a4d2e]/80 to-[#1a4d2e]"></div>
      </div>
      
      {/* Content */}
      <div className="relative h-full flex flex-col p-4 text-white">
        {/* Logo */}
        <div className="text-center mb-2">
          <h1 className="text-sm font-bold tracking-wide">MEASURED LIVESTOCK</h1>
          <p className="text-[8px] text-white/80 tracking-widest">TRAIT COLLECTION SERVICE</p>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col justify-center text-center">
          <p className="text-[#ff6b00] text-[10px] font-semibold mb-1 tracking-wider">INTRODUCING PRICEHUB</p>
          <h2 className="text-xl font-bold mb-2 leading-tight">
            Know Your<br/>
            Cost <span className="text-[#ff6b00]">Instantly</span>
          </h2>
          
          {/* Value Props */}
          <div className="flex justify-center gap-2 text-[8px] mb-2 text-white/90">
            <span>🚗 We come to you</span>
            <span>📅 Your schedule</span>
          </div>

          {/* Services */}
          <div className="grid grid-cols-2 gap-1 my-2 text-[9px]">
            <div className="bg-white/20 backdrop-blur rounded p-1.5">
              <span>⚖️</span> Weigh
            </div>
            <div className="bg-white/20 backdrop-blur rounded p-1.5 flex items-center justify-center gap-1">
              <EarTagSvg size="small" /> Tag
            </div>
            <div className="bg-white/20 backdrop-blur rounded p-1.5">
              <span>📋</span> Record
            </div>
            <div className="bg-white/20 backdrop-blur rounded p-1.5">
              <span>🔍</span> Scan
            </div>
            <div className="bg-white/20 backdrop-blur rounded p-1.5">
              <span>🧪</span> Sample
            </div>
            <div className="bg-white/20 backdrop-blur rounded p-1.5">
              <span>✅</span> Test
            </div>
          </div>

          {/* Price Preview */}
          <div className="bg-[#ff6b00] rounded-lg p-3 my-2">
            <p className="text-[8px] font-semibold mb-1">YOUR TOTAL (excl. GST)</p>
            <p className="text-2xl font-bold">$869.00</p>
            <p className="text-[8px] opacity-90">+ GST • No Hidden Costs</p>
          </div>

          <p className="text-[8px] text-white/80">
            🤝 Extra set of hands on your farm
          </p>
        </div>

        {/* CTA */}
        <div className="text-center pb-2">
          <p className="text-[10px] mb-1">👆 SWIPE UP</p>
          <div className="bg-white text-[#1a4d2e] font-bold py-2 px-4 rounded-lg text-xs">
            Plan Your Day Now
          </div>
          <p className="text-[8px] mt-2 text-white/70">measuredlivestock.com</p>
        </div>
      </div>
    </div>
  );

  // Alternative Square Ad - More Visual Impact
  const SquareAdAlt = () => (
    <div 
      className="relative overflow-hidden"
      style={{ width: '540px', height: '540px' }}
    >
      {/* Background Image Placeholder with Overlay */}
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1500595046743-cd271d694d30?w=800')] bg-cover bg-center">
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a4d2e] via-[#1a4d2e]/80 to-transparent"></div>
      </div>
      
      {/* Content */}
      <div className="relative h-full flex flex-col justify-end p-8 text-white">
        {/* Top Badge */}
        <div className="absolute top-6 left-6">
          <div className="bg-[#ff6b00] text-white text-xs font-bold px-3 py-1 rounded-full">
            NEW: PRICEHUB
          </div>
        </div>

        {/* Main Content */}
        <div className="text-center">
          <h1 className="text-lg font-bold mb-1 tracking-wide">MEASURED LIVESTOCK</h1>
          
          <h2 className="text-3xl font-bold mb-2 leading-tight">
            Instant Pricing.<br/>
            <span className="text-[#ff6b00]">Zero Surprises.</span>
          </h2>
          
          {/* Value Props */}
          <div className="flex justify-center gap-4 mb-3 text-xs">
            <span className="flex items-center gap-1">🚗 We come to you</span>
            <span className="flex items-center gap-1">📅 Your schedule</span>
            <span className="flex items-center gap-1">🤝 Extra hands</span>
          </div>

          {/* Services Pills */}
          <div className="flex flex-wrap justify-center gap-2 mb-3 text-xs">
            <span className="bg-white/20 backdrop-blur px-3 py-1 rounded-full">⚖️ Weigh</span>
            <span className="bg-white/20 backdrop-blur px-3 py-1 rounded-full inline-flex items-center gap-1"><EarTagSvg size="small" /> Tag</span>
            <span className="bg-white/20 backdrop-blur px-3 py-1 rounded-full">📋 Record</span>
            <span className="bg-white/20 backdrop-blur px-3 py-1 rounded-full">🔍 Scan</span>
            <span className="bg-white/20 backdrop-blur px-3 py-1 rounded-full">🧪 Sample</span>
            <span className="bg-white/20 backdrop-blur px-3 py-1 rounded-full">✅ Test</span>
          </div>

          <p className="text-sm text-white/90 mb-3">
            CSV files formatted for third-party software & industry platforms
          </p>

          {/* CTA */}
          <div className="inline-block bg-[#ff6b00] text-white font-bold py-3 px-8 rounded-lg text-lg">
            Plan Your Day Now →
          </div>
          <p className="text-xs mt-3 text-white/70">measuredlivestock.com</p>
        </div>
      </div>
    </div>
  );

  // Comparison Ad - Old Way vs PriceHub (Square 1080x1080)
  const ComparisonAd = () => (
    <div 
      className="relative overflow-hidden"
      style={{ width: '540px', height: '540px' }}
    >
      {/* Split Background */}
      <div className="absolute inset-0 flex">
        <div className="w-1/2 bg-gradient-to-b from-gray-700 to-gray-900"></div>
        <div className="w-1/2 bg-gradient-to-b from-[#1a4d2e] to-[#0d2818]"></div>
      </div>
      
      {/* Content */}
      <div className="relative h-full flex flex-col p-6 text-white">
        {/* Header */}
        <div className="text-center mb-4">
          <p className="text-[#ff6b00] text-sm font-bold tracking-wider mb-2">MEASURED LIVESTOCK</p>
          <h2 className="text-2xl font-bold">Still Waiting for Quotes?</h2>
        </div>

        {/* Split Comparison */}
        <div className="flex-1 flex">
          {/* Left Side - Old Way */}
          <div className="w-1/2 flex flex-col items-center justify-center px-4 border-r border-white/20">
            <div className="text-5xl mb-4">📞</div>
            <h3 className="text-lg font-bold text-gray-300 mb-3">The Old Way</h3>
            <div className="space-y-2 text-sm text-gray-400">
              <p className="flex items-center gap-2">❌ Phone tag</p>
              <p className="flex items-center gap-2">❌ Waiting days</p>
              <p className="flex items-center gap-2">❌ No price clarity</p>
              <p className="flex items-center gap-2">❌ Back & forth</p>
            </div>
            <div className="mt-4 text-3xl font-bold text-gray-500">Days...</div>
          </div>

          {/* Right Side - PriceHub */}
          <div className="w-1/2 flex flex-col items-center justify-center px-4">
            <div className="text-5xl mb-4">⚡</div>
            <h3 className="text-lg font-bold text-[#ff6b00] mb-3">PriceHub</h3>
            <div className="space-y-2 text-sm text-white/90">
              <p className="flex items-center gap-2">✅ Instant pricing</p>
              <p className="flex items-center gap-2">✅ Select one or more services</p>
              <p className="flex items-center gap-2">✅ No surprises</p>
              <p className="flex items-center gap-2">✅ We come to you</p>
            </div>
            <div className="mt-4 text-3xl font-bold text-[#ff6b00]">30 Seconds!</div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-4">
          <div className="inline-block bg-[#ff6b00] text-white font-bold py-3 px-8 rounded-lg text-base">
            Try PriceHub Now →
          </div>
          <p className="text-xs mt-2 text-white/60">measuredlivestock.com</p>
        </div>
      </div>
    </div>
  );

  // Comparison Ad - Landscape Version (1200x628)
  const ComparisonAdLandscape = () => (
    <div 
      className="relative overflow-hidden"
      style={{ width: '600px', height: '314px' }}
    >
      {/* Split Background */}
      <div className="absolute inset-0 flex">
        <div className="w-1/2 bg-gradient-to-r from-gray-800 to-gray-700"></div>
        <div className="w-1/2 bg-gradient-to-r from-[#1a4d2e] to-[#2d5a3d]"></div>
      </div>
      
      {/* Diagonal Divider */}
      <div className="absolute inset-0">
        <svg className="w-full h-full" preserveAspectRatio="none">
          <polygon points="280,0 320,314 280,314 280,0" fill="rgba(255,255,255,0.1)"/>
        </svg>
      </div>
      
      {/* Content */}
      <div className="relative h-full flex text-white">
        {/* Left Side - Old Way */}
        <div className="w-1/2 flex flex-col items-center justify-center p-4">
          <div className="text-4xl mb-2">📞</div>
          <h3 className="text-base font-bold text-gray-300 mb-2">The Old Way</h3>
          <div className="space-y-1 text-xs text-gray-400">
            <p>❌ Phone tag & voicemails</p>
            <p>❌ Waiting days for quotes</p>
            <p>❌ No price transparency</p>
          </div>
          <div className="mt-3 text-2xl font-bold text-gray-500">Days of waiting...</div>
        </div>

        {/* Right Side - PriceHub */}
        <div className="w-1/2 flex flex-col items-center justify-center p-4">
          <div className="text-4xl mb-2">⚡</div>
          <h3 className="text-base font-bold text-[#ff6b00] mb-2">PriceHub</h3>
          <div className="space-y-1 text-xs text-white/90">
            <p>✅ Instant pricing online</p>
            <p>✅ Select one or more services</p>
            <p>✅ No hidden costs</p>
          </div>
          <div className="mt-3 text-2xl font-bold text-[#ff6b00]">30 Seconds!</div>
        </div>
      </div>
      
      {/* Bottom Bar */}
      <div className="absolute bottom-0 left-0 right-0 bg-black/30 py-2 px-4 flex justify-between items-center">
        <p className="text-white text-sm font-bold">Still waiting for quotes?</p>
        <div className="bg-[#ff6b00] text-white font-bold py-1 px-4 rounded text-sm">
          Try PriceHub →
        </div>
      </div>
    </div>
  );

  // Heritage Ad Template - "Our Story" (1080x1080) with Rural Background
  const HeritageAdTemplate = () => (
    <div 
      className="relative overflow-hidden"
      style={{ width: '540px', height: '540px' }}
    >
      {/* Rural background with clean dark overlay */}
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1500595046743-cd271d694d30?w=800')] bg-cover bg-center">
        {/* Clean dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/75"></div>
      </div>
      
      {/* Content */}
      <div className="relative h-full flex flex-col p-8 text-white">
        {/* Top - Brand */}
        <div className="text-center">
          <p className="text-[#4a7c59] text-sm font-semibold tracking-widest mb-1">MEASURED LIVESTOCK</p>
          <p className="text-white/60 text-xs tracking-wider">EST. MANAWATU</p>
        </div>

        {/* Center - Heritage Message */}
        <div className="flex-1 flex flex-col justify-center text-center px-4">
          <h2 className="text-3xl font-bold mb-4 leading-tight font-serif" style={{ fontFamily: 'Georgia, serif' }}>
            Where We<br/>
            <span className="text-[#4a7c59]">Came From</span>
          </h2>
          
          <p className="text-white/90 text-sm leading-relaxed mb-4 italic">
            "Hard work, dedication, and community.<br/>
            Values passed down through generations.<br/>
            This is who we are."
          </p>

          <div className="border-t border-b border-white/20 py-3 my-2">
            <p className="text-xs text-white/70 uppercase tracking-wider">
              Farming families since generations
            </p>
          </div>
        </div>

        {/* Bottom - CTA */}
        <div className="text-center">
          <p className="text-white/70 text-xs mb-3">Modern tools. Timeless values.</p>
          <div className="inline-block bg-[#4a7c59] text-white font-bold py-3 px-8 rounded text-sm">
            Our Story →
          </div>
          <p className="text-xs mt-3 text-white/50">measuredlivestock.com</p>
        </div>
      </div>

      {/* Photo placeholder indicator */}
      <div className="absolute top-4 right-4 bg-white/10 backdrop-blur px-2 py-1 rounded text-xs text-white/70">
        📷 Add your heritage photo
      </div>
    </div>
  );

  // Heritage Ad - "Then & Now" comparison with rural background
  const HeritageThenNowAd = () => (
    <div 
      className="relative overflow-hidden"
      style={{ width: '540px', height: '540px' }}
    >
      {/* Rural background with clean dark overlay */}
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1500595046743-cd271d694d30?w=800')] bg-cover bg-center">
        {/* Clean dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/75"></div>
      </div>
      
      {/* Content */}
      <div className="relative h-full flex flex-col p-6 text-white">
        {/* Header */}
        <div className="text-center mb-4">
          <p className="text-[#4a7c59] text-sm font-semibold tracking-widest mb-1">MEASURED LIVESTOCK</p>
          <h2 className="text-2xl font-bold" style={{ fontFamily: 'Georgia, serif' }}>Same Values. <span className="text-[#4a7c59]">New Tools.</span></h2>
        </div>

        {/* Split Content */}
        <div className="flex-1 flex">
          {/* Left - Then */}
          <div className="w-1/2 flex flex-col items-center justify-center px-4 border-r border-white/20">
            <p className="text-[#4a7c59] text-xs font-semibold tracking-widest mb-2">THEN</p>
            <div className="w-24 h-24 rounded-lg bg-white/10 border-2 border-dashed border-white/30 flex items-center justify-center mb-3">
              <span className="text-3xl">📷</span>
            </div>
            <p className="text-center text-sm text-white/90 italic">
              "Grandad's yard,<br/>hard work & heart"
            </p>
            <p className="text-xs text-[#4a7c59] mt-2">Circa 1960s</p>
          </div>

          {/* Right - Now */}
          <div className="w-1/2 flex flex-col items-center justify-center px-4">
            <p className="text-[#4a7c59] text-xs font-semibold tracking-widest mb-2">NOW</p>
            <div className="w-24 h-24 rounded-lg bg-[#4a7c59]/30 flex items-center justify-center mb-3">
              <span className="text-3xl">⚡</span>
            </div>
            <p className="text-center text-sm text-white/90">
              Same dedication,<br/>modern efficiency
            </p>
            <p className="text-xs text-[#4a7c59] mt-2">PriceHub 2025</p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-4">
          <p className="text-white/70 text-xs mb-3 italic">The values that built our farms. The tools that serve them today.</p>
          <div className="inline-block bg-[#4a7c59] text-white font-bold py-2 px-6 rounded text-sm">
            Experience PriceHub →
          </div>
          <p className="text-xs mt-3 text-white/50">measuredlivestock.com</p>
        </div>
      </div>
    </div>
  );

  // Animated 10-Second Video Ad - How PriceHub Works
  const AnimatedVideoAd = () => {
    const [step, setStep] = useState(0);
    const [animationKey, setAnimationKey] = useState(0);
    
    // Total duration: 10 seconds
    // Step 0: Intro (0-2s)
    // Step 1: Select Services (2-4.5s)
    // Step 2: See Price (4.5-7s)
    // Step 3: Book Now (7-9s)
    // Step 4: CTA/Logo (9-10s)
    
    useEffect(() => {
      const timings = [2000, 2500, 2500, 2000, 1000]; // ms for each step
      
      const runAnimation = () => {
        let currentStep = 0;
        setStep(0);
        
        const advanceStep = () => {
          if (currentStep < 4) {
            setTimeout(() => {
              currentStep++;
              setStep(currentStep);
              advanceStep();
            }, timings[currentStep]);
          } else {
            // Reset and loop after 10 seconds total
            setTimeout(() => {
              setAnimationKey(prev => prev + 1);
            }, timings[4]);
          }
        };
        
        advanceStep();
      };
      
      runAnimation();
    }, [animationKey]);

    const services = [
      { name: 'Weighing', icon: '⚖️', price: 2.50 },
      { name: '(DNA) Tissue Sampling', icon: '🧬', price: 8.00 },
      { name: 'Herd Audit', icon: '📋', price: 3.50 },
    ];

    const selectedServices = step >= 1 ? [0, 1] : [];
    const showPrice = step >= 2;
    const totalPrice = showPrice ? (120 + 250 + (2.50 * 100) + (8.00 * 100)).toFixed(2) : '0.00';

    return (
      <div 
        key={animationKey}
        className="relative bg-gradient-to-br from-[#1a4d2e] via-[#2d5a3d] to-[#1a4d2e] text-white overflow-hidden"
        style={{ width: '540px', height: '540px' }}
      >
        {/* Animated background circles */}
        <div className="absolute inset-0 overflow-hidden">
          <div className={`absolute -top-20 -right-20 w-64 h-64 bg-white/5 rounded-full transition-all duration-1000 ${step >= 1 ? 'scale-150' : 'scale-100'}`}></div>
          <div className={`absolute -bottom-32 -left-32 w-80 h-80 bg-white/5 rounded-full transition-all duration-1000 ${step >= 2 ? 'scale-125' : 'scale-100'}`}></div>
        </div>

        {/* Content */}
        <div className="relative h-full flex flex-col p-8">
          
          {/* Header - Always visible */}
          <div className={`text-center transition-all duration-500 ${step === 0 ? 'opacity-100 translate-y-0' : 'opacity-80 -translate-y-2'}`}>
            <p className="text-orange-400 text-sm font-semibold tracking-widest mb-1">MEASURED LIVESTOCK</p>
            <div className={`transition-all duration-700 ${step === 0 ? 'text-3xl' : 'text-xl'}`}>
              <span className="font-bold">Price</span><span className="text-orange-400 font-bold">Hub</span>
            </div>
          </div>

          {/* Step 0: Intro */}
          {step === 0 && (
            <div className="flex-1 flex flex-col items-center justify-center animate-fade-in">
              <h2 className="text-3xl font-bold text-center mb-4 animate-slide-up">
                Know Your Cost<br/>
                <span className="text-orange-400">In Seconds</span>
              </h2>
              <p className="text-white/70 text-center">See how it works →</p>
            </div>
          )}

          {/* Step 1: Select Services */}
          {step === 1 && (
            <div className="flex-1 flex flex-col justify-center animate-fade-in">
              <p className="text-center text-white/70 text-sm mb-4 uppercase tracking-wider">Step 1</p>
              <h3 className="text-2xl font-bold text-center mb-6">Select Your Services</h3>
              
              <div className="space-y-3 px-4">
                {services.map((service, idx) => (
                  <div 
                    key={service.name}
                    className={`flex items-center justify-between p-3 rounded-lg transition-all duration-500 ${
                      selectedServices.includes(idx) 
                        ? 'bg-orange-500 scale-105' 
                        : 'bg-white/10'
                    }`}
                    style={{ 
                      animationDelay: `${idx * 200}ms`,
                      animation: selectedServices.includes(idx) ? 'pulse 0.5s ease-out' : 'none'
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{service.icon}</span>
                      <span className="font-semibold">{service.name}</span>
                    </div>
                    {selectedServices.includes(idx) && (
                      <span className="text-white font-bold">✓</span>
                    )}
                  </div>
                ))}
              </div>
              
              <p className="text-center text-white/50 text-xs mt-4">Tap to select services</p>
            </div>
          )}

          {/* Step 2: See Price */}
          {step === 2 && (
            <div className="flex-1 flex flex-col justify-center items-center animate-fade-in">
              <p className="text-center text-white/70 text-sm mb-4 uppercase tracking-wider">Step 2</p>
              <h3 className="text-2xl font-bold text-center mb-6">See Your Price Instantly</h3>
              
              <div className="bg-white rounded-2xl p-6 text-center shadow-2xl transform animate-scale-in">
                <p className="text-gray-500 text-sm mb-2">YOUR TOTAL (EXCL. GST)</p>
                <div className="text-5xl font-bold text-[#1a4d2e] animate-count-up">
                  ${totalPrice}
                </div>
                <div className="mt-3 text-xs text-gray-400">
                  <p>Establishment: $370 | Per head: $10.50 × 100</p>
                </div>
              </div>
              
              <p className="text-center text-orange-400 text-sm mt-4 font-semibold">
                ⚡ No waiting. No phone calls.
              </p>
            </div>
          )}

          {/* Step 3: Book Now */}
          {step === 3 && (
            <div className="flex-1 flex flex-col justify-center items-center animate-fade-in">
              <p className="text-center text-white/70 text-sm mb-4 uppercase tracking-wider">Step 3</p>
              <h3 className="text-2xl font-bold text-center mb-6">Book Your Visit</h3>
              
              <div className="space-y-4 w-full max-w-xs">
                <div className="bg-white/10 rounded-lg p-4 flex items-center gap-3">
                  <span className="text-2xl">📅</span>
                  <div>
                    <p className="font-semibold">Choose Your Date</p>
                    <p className="text-white/60 text-sm">We work around your schedule</p>
                  </div>
                </div>
                <div className="bg-white/10 rounded-lg p-4 flex items-center gap-3">
                  <span className="text-2xl">🚗</span>
                  <div>
                    <p className="font-semibold">We Come to You</p>
                    <p className="text-white/60 text-sm">On-farm service</p>
                  </div>
                </div>
              </div>
              
              <button className="mt-6 bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-8 rounded-lg text-lg animate-pulse">
                Book Now →
              </button>
            </div>
          )}

          {/* Step 4: CTA */}
          {step === 4 && (
            <div className="flex-1 flex flex-col justify-center items-center animate-fade-in">
              <div className="text-center">
                <h2 className="text-3xl font-bold mb-2">
                  Try <span className="text-orange-400">PriceHub</span> Today
                </h2>
                <p className="text-white/70 mb-6">Free to use. No obligation.</p>
                
                <button className="bg-orange-500 text-white font-bold py-4 px-10 rounded-lg text-xl shadow-lg transform hover:scale-105 transition-transform">
                  Get Started →
                </button>
                
                <p className="text-xs mt-6 text-white/50">measuredlivestock.com</p>
              </div>
            </div>
          )}

          {/* Progress indicator */}
          <div className="flex justify-center gap-2 mt-4">
            {[0, 1, 2, 3, 4].map((i) => (
              <div 
                key={i}
                className={`h-1 rounded-full transition-all duration-300 ${
                  i === step ? 'w-8 bg-orange-400' : 'w-2 bg-white/30'
                }`}
              />
            ))}
          </div>

          {/* Duration badge */}
          <div className="absolute top-4 right-4 bg-black/30 backdrop-blur px-2 py-1 rounded text-xs">
            🎬 10s Video Ad
          </div>
        </div>

        {/* CSS Animations */}
        <style>{`
          @keyframes fade-in {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slide-up {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes scale-in {
            from { opacity: 0; transform: scale(0.8); }
            to { opacity: 1; transform: scale(1); }
          }
          .animate-fade-in {
            animation: fade-in 0.5s ease-out forwards;
          }
          .animate-slide-up {
            animation: slide-up 0.6s ease-out forwards;
          }
          .animate-scale-in {
            animation: scale-in 0.5s ease-out forwards;
          }
        `}</style>
      </div>
    );
  };

  // Square format for Stories/Reels (9:16 aspect ratio - 1080x1920)
  const AnimatedStoryAd = () => {
    const [step, setStep] = useState(0);
    const [animationKey, setAnimationKey] = useState(0);
    
    useEffect(() => {
      const timings = [2000, 2500, 2500, 2000, 1000];
      
      const runAnimation = () => {
        let currentStep = 0;
        setStep(0);
        
        const advanceStep = () => {
          if (currentStep < 4) {
            setTimeout(() => {
              currentStep++;
              setStep(currentStep);
              advanceStep();
            }, timings[currentStep]);
          } else {
            setTimeout(() => {
              setAnimationKey(prev => prev + 1);
            }, timings[4]);
          }
        };
        
        advanceStep();
      };
      
      runAnimation();
    }, [animationKey]);

    const services = [
      { name: 'Weighing', icon: '⚖️' },
      { name: '(DNA) Tissue Sampling', icon: '🧬' },
    ];

    return (
      <div 
        key={animationKey}
        className="relative bg-gradient-to-b from-[#1a4d2e] via-[#2d5a3d] to-[#1a4d2e] text-white overflow-hidden"
        style={{ width: '270px', height: '480px' }}
      >
        {/* Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className={`absolute -top-10 -right-10 w-32 h-32 bg-white/5 rounded-full transition-all duration-1000 ${step >= 1 ? 'scale-150' : 'scale-100'}`}></div>
        </div>

        <div className="relative h-full flex flex-col p-5">
          {/* Header */}
          <div className="text-center mb-4">
            <p className="text-orange-400 text-xs font-semibold tracking-widest">MEASURED LIVESTOCK</p>
            <div className="text-lg font-bold">
              <span>Price</span><span className="text-orange-400">Hub</span>
            </div>
          </div>

          {/* Step 0: Intro */}
          {step === 0 && (
            <div className="flex-1 flex flex-col items-center justify-center">
              <h2 className="text-2xl font-bold text-center mb-3">
                Know Your Cost<br/>
                <span className="text-orange-400">Instantly</span>
              </h2>
              <p className="text-white/60 text-sm">Swipe to see how →</p>
            </div>
          )}

          {/* Step 1: Select */}
          {step === 1 && (
            <div className="flex-1 flex flex-col justify-center">
              <p className="text-xs text-white/60 text-center mb-2">STEP 1</p>
              <h3 className="text-xl font-bold text-center mb-4">Select Services</h3>
              <div className="space-y-2">
                {services.map((s, i) => (
                  <div key={i} className="bg-orange-500 rounded-lg p-3 flex items-center gap-2">
                    <span>{s.icon}</span>
                    <span className="font-semibold text-sm">{s.name}</span>
                    <span className="ml-auto">✓</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Price */}
          {step === 2 && (
            <div className="flex-1 flex flex-col justify-center items-center">
              <p className="text-xs text-white/60 mb-2">STEP 2</p>
              <h3 className="text-xl font-bold mb-4">See Your Price</h3>
              <div className="bg-white rounded-xl p-5 text-center">
                <p className="text-gray-500 text-xs mb-1">TOTAL (EXCL. GST)</p>
                <p className="text-4xl font-bold text-[#1a4d2e]">$1,420</p>
              </div>
              <p className="text-orange-400 text-xs mt-3">⚡ No waiting!</p>
            </div>
          )}

          {/* Step 3: Book */}
          {step === 3 && (
            <div className="flex-1 flex flex-col justify-center items-center">
              <p className="text-xs text-white/60 mb-2">STEP 3</p>
              <h3 className="text-xl font-bold mb-4">Book Your Visit</h3>
              <div className="space-y-2 w-full">
                <div className="bg-white/10 rounded-lg p-3 flex items-center gap-2">
                  <span>📅</span>
                  <span className="text-sm">Pick your date</span>
                </div>
                <div className="bg-white/10 rounded-lg p-3 flex items-center gap-2">
                  <span>🚗</span>
                  <span className="text-sm">We come to you</span>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: CTA */}
          {step === 4 && (
            <div className="flex-1 flex flex-col justify-center items-center">
              <h2 className="text-2xl font-bold text-center mb-4">
                Try <span className="text-orange-400">PriceHub</span>
              </h2>
              <button className="bg-orange-500 text-white font-bold py-3 px-6 rounded-lg">
                Get Started →
              </button>
              <p className="text-xs mt-4 text-white/50">measuredlivestock.com</p>
            </div>
          )}

          {/* Progress dots */}
          <div className="flex justify-center gap-1 mt-4">
            {[0, 1, 2, 3, 4].map((i) => (
              <div 
                key={i}
                className={`h-1 rounded-full transition-all duration-300 ${
                  i === step ? 'w-6 bg-orange-400' : 'w-1.5 bg-white/30'
                }`}
              />
            ))}
          </div>

          {/* Badge */}
          <div className="absolute top-3 right-3 bg-black/30 px-2 py-0.5 rounded text-xs">
            🎬 10s
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* ═══ AD TEMPLATES — render active templates, hide archived ═══ */}
      {AD_TEMPLATES.filter(t => !archived.includes(t.key)).map(({ key, label, Component, fields }) => (
        <div key={key} className="relative" data-testid={`ad-template-${key}`}>
          <AdControls
            templateKey={key}
            fields={fields}
            values={overrides[key] || {}}
            onSave={(patch) => saveOverrides(key, patch)}
            onArchive={() => archive(key)}
          />
          <Component overrides={overrides[key] || {}} />
        </div>
      ))}

      {/* Archived templates section (if any) */}
      {archived.length > 0 && (
        <div className="bg-white border-t border-gray-200 py-6 px-4">
          <div className="max-w-6xl mx-auto">
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-3">
              Archived templates ({archived.length})
            </h3>
            <div className="flex flex-wrap gap-2">
              {archived.map(k => {
                const meta = AD_TEMPLATES.find(t => t.key === k);
                return (
                  <button
                    key={k}
                    onClick={() => unarchive(k)}
                    data-testid={`restore-${k}`}
                    className="flex items-center gap-1.5 bg-gray-100 hover:bg-[#1a4d2e] hover:text-white text-gray-700 text-xs font-bold px-3 py-1.5 rounded-full border border-gray-300 transition-all"
                  >
                    <RotateCcw className="w-3 h-3" />
                    Restore: {meta?.label || k}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="p-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <button 
            onClick={() => navigate('/')}
            className="text-[#1a4d2e] hover:underline mb-4"
          >
            ← Back to Home
          </button>
          <h1 className="text-3xl font-bold text-[#1a4d2e]">Social Media Ads</h1>
          <p className="text-gray-600">Right-click and save images, or take screenshots of the ads below</p>
        </div>

        {/* Square Ad */}
        <div className="mb-12">
          <h2 className="text-xl font-semibold mb-2 text-gray-800">Facebook/Instagram Feed - Square (1080x1080)</h2>
          <p className="text-sm text-gray-500 mb-4">Displayed at 50% scale - actual size is 1080x1080px</p>
          <div className="inline-block shadow-2xl">
            <SquareAd />
          </div>
        </div>

        {/* 10-Second Video Ads Section */}
        <div className="mb-12 bg-gradient-to-r from-purple-50 to-orange-50 p-6 rounded-xl border-2 border-purple-200">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">🎬</span>
            <h2 className="text-xl font-semibold text-gray-800">10-Second Video Ads - "How PriceHub Works"</h2>
            <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full animate-pulse">LIVE</span>
          </div>
          <p className="text-sm text-gray-600 mb-6">Auto-playing animated ads showing the PriceHub booking flow. Perfect for Facebook/Instagram video ads and Stories.</p>
          
          <div className="flex flex-wrap gap-8 items-start">
            {/* Square Video Ad */}
            <div>
              <h3 className="text-lg font-semibold mb-2 text-gray-700">📱 Feed Ad (1080×1080)</h3>
              <p className="text-xs text-gray-500 mb-3">For Facebook & Instagram Feed</p>
              <div className="inline-block shadow-2xl rounded-lg overflow-hidden">
                <AnimatedVideoAd />
              </div>
              <button
                onClick={() => saveAdToMarketing('10s Video Ad - Feed', '10-Second Animated Video', 'Facebook/Instagram Feed')}
                disabled={savingAd === '10s Video Ad - Feed' || savedAds.includes('10s Video Ad - Feed')}
                className={`mt-3 w-full py-2 px-4 rounded-lg font-semibold text-sm transition-all ${
                  savedAds.includes('10s Video Ad - Feed')
                    ? 'bg-green-500 text-white cursor-default'
                    : savingAd === '10s Video Ad - Feed'
                    ? 'bg-gray-400 text-white cursor-wait'
                    : 'bg-[#1a4d2e] text-white hover:bg-[#2d5a3d]'
                }`}
              >
                {savedAds.includes('10s Video Ad - Feed') ? '✓ Saved to Admin' : savingAd === '10s Video Ad - Feed' ? 'Saving...' : '💾 Save to Admin Marketing'}
              </button>
            </div>
            
            {/* Story Video Ad */}
            <div>
              <h3 className="text-lg font-semibold mb-2 text-gray-700">📲 Story/Reel Ad (1080×1920)</h3>
              <p className="text-xs text-gray-500 mb-3">For Instagram Stories & Reels</p>
              <div className="inline-block shadow-2xl rounded-lg overflow-hidden">
                <AnimatedStoryAd />
              </div>
              <button
                onClick={() => saveAdToMarketing('10s Video Ad - Story', '10-Second Animated Video', 'Instagram Stories/Reels')}
                disabled={savingAd === '10s Video Ad - Story' || savedAds.includes('10s Video Ad - Story')}
                className={`mt-3 w-full py-2 px-4 rounded-lg font-semibold text-sm transition-all ${
                  savedAds.includes('10s Video Ad - Story')
                    ? 'bg-green-500 text-white cursor-default'
                    : savingAd === '10s Video Ad - Story'
                    ? 'bg-gray-400 text-white cursor-wait'
                    : 'bg-[#1a4d2e] text-white hover:bg-[#2d5a3d]'
                }`}
              >
                {savedAds.includes('10s Video Ad - Story') ? '✓ Saved to Admin' : savingAd === '10s Video Ad - Story' ? 'Saving...' : '💾 Save to Admin Marketing'}
              </button>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-white/70 rounded-lg">
            <p className="text-sm text-gray-700 font-semibold mb-2">💡 How to use:</p>
            <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
              <li>Use screen recording software to capture the animation</li>
              <li>Record for 10 seconds (one full loop)</li>
              <li>Upload to Facebook/Instagram as a video ad</li>
              <li>Add captions for better engagement</li>
            </ol>
          </div>
        </div>

        {/* Alternative Square Ad */}
        <div className="mb-12">
          <h2 className="text-xl font-semibold mb-2 text-gray-800">Facebook/Instagram Feed - Square Alt (1080x1080)</h2>
          <p className="text-sm text-gray-500 mb-4">With background image - Displayed at 50% scale</p>
          <div className="inline-block shadow-2xl">
            <SquareAdAlt />
          </div>
        </div>

        {/* Comparison Ad - Square */}
        <div className="mb-12">
          <h2 className="text-xl font-semibold mb-2 text-gray-800">🆕 Comparison Ad - "Old Way vs PriceHub" (1080x1080)</h2>
          <p className="text-sm text-gray-500 mb-4">Split screen comparison - Displayed at 50% scale</p>
          <div className="inline-block shadow-2xl">
            <ComparisonAd />
          </div>
        </div>

        {/* Comparison Ad - Landscape */}
        <div className="mb-12">
          <h2 className="text-xl font-semibold mb-2 text-gray-800">🆕 Comparison Ad - Landscape (1200x628)</h2>
          <p className="text-sm text-gray-500 mb-4">Split screen comparison for Facebook Feed - Displayed at 50% scale</p>
          <div className="inline-block shadow-2xl">
            <ComparisonAdLandscape />
          </div>
        </div>

        {/* Landscape Ad */}
        <div className="mb-12">
          <h2 className="text-xl font-semibold mb-2 text-gray-800">Facebook Feed - Landscape (1200x628)</h2>
          <p className="text-sm text-gray-500 mb-4">Displayed at 50% scale - actual size is 1200x628px</p>
          <div className="inline-block shadow-2xl">
            <LandscapeAd />
          </div>
        </div>

        {/* Story Ad */}
        <div className="mb-12">
          <h2 className="text-xl font-semibold mb-2 text-gray-800">Instagram/Facebook Stories (1080x1920)</h2>
          <p className="text-sm text-gray-500 mb-4">Displayed at 25% scale - actual size is 1080x1920px</p>
          <div className="inline-block shadow-2xl">
            <StoryAd />
          </div>
        </div>

        {/* Heritage Section Divider */}
        <div className="border-t-4 border-[#d4a574] pt-8 mb-8">
          <h2 className="text-2xl font-bold text-[#3d2914] mb-2">🏛️ Heritage Campaign Templates</h2>
          <p className="text-gray-600">Tell your family farming story with these heritage-themed designs. Add your own photos from the Heritage Library.</p>
        </div>

        {/* Heritage Ad - Our Story */}
        <div className="mb-12">
          <h2 className="text-xl font-semibold mb-2 text-gray-800">🆕 Heritage Ad - "Our Story" (1080x1080)</h2>
          <p className="text-sm text-gray-500 mb-4">Rural theme - Add your heritage photo as background</p>
          <div className="inline-block shadow-2xl">
            <HeritageAdTemplate />
          </div>
        </div>

        {/* Heritage Ad - Then & Now */}
        <div className="mb-12">
          <h2 className="text-xl font-semibold mb-2 text-gray-800">🆕 Heritage Ad - "Then & Now" (1080x1080)</h2>
          <p className="text-sm text-gray-500 mb-4">Split comparison: Heritage photo (left) vs Modern PriceHub (right)</p>
          <div className="inline-block shadow-2xl">
            <HeritageThenNowAd />
          </div>
        </div>

        {/* Ad Copy */}
        <div className="bg-white rounded-lg p-6 shadow-lg mb-12">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">📝 Suggested Ad Copy</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-[#1a4d2e] mb-2">Primary Text (Facebook/Instagram)</h3>
              <div className="bg-gray-50 p-4 rounded-lg text-sm">
                <p>🐄 Tired of waiting for quotes?</p>
                <p className="mt-2">With PriceHub, you can select one or more services and see your price INSTANTLY. No phone calls. No waiting. No surprises.</p>
                <p className="mt-2">🚗 We come to you • 📅 Work around YOUR schedule • 🤝 Extra set of hands on your farm</p>
                <p className="mt-2">✅ Weigh • Tag • Record • Scan • Sample • Test</p>
                <p className="mt-2">📊 CSV files formatted for third-party software & industry platforms</p>
                <p className="mt-2 font-semibold">👉 Plan your day now at measuredlivestock.com</p>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-[#d4a574] mb-2">Heritage Campaign Copy</h3>
              <div className="bg-[#f9f5f0] p-4 rounded-lg text-sm border border-[#d4a574]/30">
                <p>🏛️ Where we came from shapes who we are today.</p>
                <p className="mt-2">Hard work. Dedication. Community. Values passed down through generations of farming families.</p>
                <p className="mt-2">We bring those same values to your farm - with modern tools that respect your time and your schedule.</p>
                <p className="mt-2 font-semibold">👉 Discover our story at measuredlivestock.com</p>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-[#1a4d2e] mb-2">Short Text (Stories/Reels)</h3>
              <div className="bg-gray-50 p-4 rounded-lg text-sm">
                <p>Know your cost INSTANTLY ⚡</p>
                <p>🚗 We come to you • 📅 Your schedule • 🤝 Extra hands</p>
                <p>Weigh • Tag • Record • Scan • Sample • Test</p>
                <p>Swipe up to plan your day 👆</p>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-[#1a4d2e] mb-2">Headline Options</h3>
              <div className="bg-gray-50 p-4 rounded-lg text-sm space-y-1">
                <p>• "Instant Pricing for Your Farm"</p>
                <p>• "Select One or More Services. See Prices Instantly."</p>
                <p>• "We Come to You - Your Schedule, Your Farm"</p>
                <p>• "An Extra Set of Hands When You Need It"</p>
                <p>• "No Waiting. No Surprises."</p>
              </div>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-[#fff8f0] border-l-4 border-[#ff6b00] p-4 rounded-r">
          <h3 className="font-semibold text-[#1a4d2e] mb-2">💡 How to Use These Ads</h3>
          <ol className="text-sm text-gray-700 space-y-1 list-decimal ml-4">
            <li>Take a screenshot of the ad design you want</li>
            <li>Crop to remove any extra space</li>
            <li>Upload to Facebook/Instagram Ads Manager</li>
            <li>Use the suggested ad copy above</li>
            <li>Set your target audience (farmers, rural areas, livestock industry)</li>
          </ol>
        </div>
      </div>
      </div>
    </div>
  );
};

export default SocialAdsPage;
